<template>
    <div class="form__container">
        <div class="posInfo">
            <ad-icon :icon="'pin'" variant="white"></ad-icon>
            <span v-if="accessOperations.details?.pos_name">{{ accessOperations.details?.pos_name }}</span>
        </div>
        <div v-if="accessOperations.details?.products_show">
            <ads-list></ads-list>
        </div>
        <div v-if="accessOperations.details?.diffusion_zone_show">
            <diffusion-zone></diffusion-zone>
        </div>
        <div class="operation-theme__container">
            <p class="fsb-typo-heading-5">{{ $t('operationTheme') }} :</p>
            <div class="operation-theme">
                <div class="operation-theme__img">
                    <img :src="accessOperations.details?.crea.image_url" alt="banner preview" />
                </div>
                <p class="operation-theme__text">{{ accessOperations.details?.crea.name }}</p>
            </div>
        </div>
        <div class="agents__container" v-if="accessOperations.details?.agents_list">
            <p class="fsb-typo-heading-5">{{ $t('selectAgents') }}</p>
            <base-checkbox-all name="agents" v-model:options="agentsOptions" />
        </div>
        <div>
            <div class="third-bloc">
                <budget-slider v-if="accessOperations.details?.slider_show"></budget-slider>
                <budgets-recap></budgets-recap>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import AdsList from '../Campaigns/AdsList.vue';
    import BudgetsRecap from '../Campaigns/BudgetsRecap.vue';
    import BudgetSlider from '../Campaigns/BudgetSlider.vue';
    import DiffusionZone from '../Campaigns/DiffusionZone.vue';
    import { storeToRefs } from 'pinia';
    import { onMounted, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { useAccessOperationsStore } from '@/store/accessOperations';
    import { useAccessDataStore } from '@/store/accessData';
    import { CheckboxAllOption } from '@/interfaces/CheckboxAllOptions';
    import BaseCheckboxAll from '../shared/BaseCheckboxAll.vue';

    const route = useRoute();
    const router = useRouter();

    const accessOperationsStore = useAccessOperationsStore();
    const accessDataStore = useAccessDataStore();

    const { accessOperations } = storeToRefs(accessOperationsStore);
    const { minBudget, budget } = storeToRefs(accessDataStore);

    const agentsOptions = ref<CheckboxAllOption<{ amount: number }>[]>([]);

    watch(
        () => accessOperations.value.details?.agents_list,
        (agentsList) => {
            if (!agentsList) return;
            agentsOptions.value = Object.keys(agentsList).map((code) => ({
                text: agentsList[code].name,
                subLabel: formatEuro(agentsList[code].amount),
                amount: agentsList[code].amount,
                code: code,
                value: agentsList[code].amount.toString(),
                checked: false,
                disabled: agentsList[code].status !== 'active',
            }));
        }
    );

    watch(agentsOptions, (options) => {
        const checkedAgents = options?.filter((option) => option.checked && !option.disabled);
        accessDataStore.setSelectedAgents(checkedAgents);
    });

    const formatEuro = (amount: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
        }).format(amount / 100);
    };

    onMounted(async () => {
        const token = route.params.token as string;
        try {
            await accessOperationsStore.getAccessOperations(token);
            if (!Object.keys(accessOperations.value.details?.agents_list ?? {}).length) budget.value = minBudget.value;
        } catch {
            router.push({ name: 'error' });
        }
    });
</script>

<style lang="scss">
    li {
        color: white !important;
        font-weight: 700 !important;
    }
    h5 {
        color: white !important;
        font-weight: 700 !important;
        line-height: 18px !important;
        margin-bottom: 10px !important;
    }
    .form__container {
        display: flex;
        flex-direction: column;
        gap: 24px;
        width: 100%;
    }
    .sub-title {
        color: white !important;
        font-weight: 700 !important;
        span {
            color: red;
        }
    }
    .posInfo {
        font-weight: 700;
        color: white !important;
        display: grid;
        justify-content: center;
        // margin-bottom: 25px;
        gap: 8px;

        .fsb-icon {
            display: flex;
            justify-content: center;
        }
    }
    .agents__container {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .fsb-typo-heading-5 {
            color: white;
        }

        & .fsb-base-checkbox {
            padding: 12px;

            & + .fsb-base-checkbox {
                border-top: 1px solid var(--grayscale-background);
            }

            &.--with-sub-label {
                margin-bottom: 0;
            }

            &-all + div {
                border-radius: 4px;
                background-color: white;
                height: fit-content;
                max-height: calc(49px * 6);
                overflow-y: scroll;
            }
        }

        label {
            align-items: center;
            justify-content: space-between;
        }

        & .sub-label {
            position: static !important;
        }
    }

    #fsb-base-checkbox-agents {
        & + label > .fsb-typo-btn-principal {
            color: white;
        }

        & + label.custom-control-label:before {
            border-color: white;
        }
    }
    .operation-theme {
        display: flex;
        gap: 8px;
        align-items: center;

        &__container {
            display: flex;
            flex-direction: column;
            gap: 8px;

            &,
            .fsb-typo-heading-5 {
                color: white;
            }
        }

        &__img {
            aspect-ratio: 1 / 1;
            max-width: 48px;
            width: 100%;
            border-radius: 2px;
            border: 1px solid white;

            img {
                display: block;
                width: 100%;
                max-width: 100%;
                height: 100%;
                max-height: 100%;
                object-fit: cover;
            }
        }
    }
</style>
